import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSmoothScroll } from "@/hooks/use-smooth-scroll";
import { DURATION, fadeInUp, staggerContainer } from "@/lib/animations";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, Facebook, Linkedin, Mail, MapPin, Phone, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";

const socialLinks = [
  { icon: Linkedin, href: "https://www.linkedin.com/company/ibtkary/", label: "LinkedIn" },
  { icon: Facebook, href: "https://www.facebook.com/ibtkary/", label: "Facebook" },
];

export default function Footer() {
  const scrollTo = useSmoothScroll();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement newsletter subscription
    console.log("Newsletter subscription:", email);
    setEmail("");
  };

  const services = [
    { key: "mobileAppDev", href: "/services" },
    { key: "webDev", href: "/services" },
    { key: "backendDev", href: "/services" },
    { key: "aiSolutions", href: "/services" },
    { key: "designBranding", href: "/services" },
  ];

  const company = [
    { key: "footerAboutUs", href: "/about" },
    { key: "footerPortfolio", href: "/projects" },
    { key: "footerContact", href: "/#contact" },
  ];

  return (
    <>
      <footer className="bg-card border-t border-border">
        {/* Newsletter Section */}
        <div className="border-b border-border/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <motion.div
              className="flex flex-col lg:flex-row items-center justify-between gap-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: DURATION.slow }}
              viewport={{ once: true }}
            >
              <div className={`text-center lg:text-${isRTL ? 'right' : 'left'} max-w-md`}>
                <div className={`flex items-center gap-3 mb-3 justify-center lg:justify-${isRTL ? 'end' : 'start'}`}>
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">
                    {t("newsletterTitle")}
                  </h3>
                </div>
                <p className="text-muted-foreground">
                  {t("newsletterDescription")}
                </p>
              </div>

              <form
                onSubmit={handleNewsletterSubmit}
                className={`flex flex-col sm:flex-row gap-3 w-full lg:w-auto ${isRTL ? 'sm:flex-row-reverse' : ''}`}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("emailPlaceholder")}
                  className="h-12 w-full sm:w-80 bg-background rounded-xl"
                  required
                />
                <Button type="submit" className="h-12 px-6 rounded-xl">
                  {t("subscribe")}
                  <Send className={`h-4 w-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                </Button>
              </form>
            </motion.div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {/* Brand Column */}
            <motion.div variants={fadeInUp} className="lg:col-span-1">
              <Link href="/">
                <button
                  className="hover:opacity-80 transition-opacity mb-6 block"
                >
                  <img
                    src="/assets/logo.webp"
                    alt="Ibtkary Logo"
                    className="h-14 w-auto object-contain"
                  />
                </button>
              </Link>
              <p className="text-sm font-medium leading-relaxed text-foreground mb-3" dir={isRTL ? 'rtl' : 'ltr'}>
                {t("brandTagline")}
              </p>
              <p
                className={`text-muted-foreground text-sm leading-relaxed mb-6 ${isRTL ? 'text-right' : ''}`}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                {t("footerDescription")}
              </p>
              <div className="flex gap-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                    aria-label={social.label}
                  >
                    <social.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Services Column */}
            <motion.div variants={fadeInUp} dir={isRTL ? 'rtl' : 'ltr'}>
              <h4 className={`text-foreground font-semibold mb-6 ${isRTL ? 'text-right' : ''}`}>
                {t("footerServices")}
              </h4>
              <ul className="space-y-4">
                {services.map((service) => (
                  <li key={service.key}>
                    <Link href={service.href}>
                      <span className="text-muted-foreground hover:text-primary transition-colors duration-200 text-sm cursor-pointer">
                        {t(service.key)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Company Column */}
            <motion.div variants={fadeInUp} dir={isRTL ? 'rtl' : 'ltr'}>
              <h4 className={`text-foreground font-semibold mb-6 ${isRTL ? 'text-right' : ''}`}>
                {t("footerCompany")}
              </h4>
              <ul className="space-y-4">
                {company.map((item) => (
                  <li key={item.key}>
                    <Link href={item.href}>
                      <span className="text-muted-foreground hover:text-primary transition-colors duration-200 text-sm cursor-pointer">
                        {t(item.key)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Column */}
            <motion.div variants={fadeInUp} dir={isRTL ? 'rtl' : 'ltr'}>
              <h4 className={`text-foreground font-semibold mb-6 ${isRTL ? 'text-right' : ''}`}>
                {t("footerGetInTouch")}
              </h4>
              <ul className={`space-y-4 text-sm text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
                <li>
                  <a
                    href="mailto:info@Ibtkary.com"
                    className="flex items-center gap-3 hover:text-primary transition-colors"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  >
                    <Mail className="h-4 w-4 text-primary shrink-0" />
                    <span dir="ltr">info@Ibtkary.com</span>
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@Ibtkary.net"
                    className="flex items-center gap-3 hover:text-primary transition-colors"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  >
                    <Mail className="h-4 w-4 text-primary shrink-0" />
                    <span dir="ltr">info@Ibtkary.net</span>
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+201068338151"
                    className="flex items-center gap-3 hover:text-primary transition-colors"
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  >
                    <Phone className="h-4 w-4 text-primary shrink-0" />
                    <span dir="ltr">+201068338151</span>
                  </a>
                </li>
                <li
                  className="flex items-start gap-3"
                  style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                >
                  <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>{t("locationDetails1")}</span>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>

        {/* Copyright Bar */}
        <div className="border-t border-border/50">
          <div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-muted-foreground text-sm">
                {t("footerCopyright")}
              </p>
              <p className="text-muted-foreground text-xs flex items-center gap-1">
                {isRTL ? "صنع بـ" : "Made with"}
                <span className="text-red-500">❤️</span>
                {isRTL ? "بواسطة فريق ابتكاري" : "by Ibtkary Team"}
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            className={`fixed bottom-6 ${isRTL ? 'left-6' : 'right-6'} z-50 w-12 h-12 rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/25 flex items-center justify-center hover:bg-primary/90 transition-colors`}
            aria-label="Back to top"
          >
            <ArrowUp className="h-5 w-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
